import { useState, useRef, useEffect, useLayoutEffect, useCallback } from 'react';
import { useInfiniteQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faExpand, faCompress, faPaperPlane } from '@fortawesome/free-solid-svg-icons';
import { api } from '@/lib/axios';
import { MessageBubble } from '@/components/Chat/MessageBubble';
import { ThinkingAnimation } from '@/components/Chat/ThinkingAnimation';
import type { Message, ChatResponse, PaginatedMessages } from '@/types/chat';
import styles from './DealChatSidebar.module.css';

interface DealChatSidebarProps {
  sessionId: string | null;
  dealTitle: string;
  onClose?: () => void;
  onToggleExpand?: () => void;
  isExpanded?: boolean;
}

export const DealChatSidebar = ({
  sessionId,
  dealTitle,
  onClose,
  onToggleExpand,
  isExpanded,
}: DealChatSidebarProps) => {
  const [input, setInput] = useState('');
  const [localMessages, setLocalMessages] = useState<Message[]>([]);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const isInitialMount = useRef(true);
  const queryClient = useQueryClient();

  const { data: messagesData, isLoading: messagesLoading } = useInfiniteQuery({
    queryKey: ['deal-chat-messages', sessionId],
    queryFn: async ({ pageParam = 1 }) => {
      if (!sessionId) throw new Error('No session');
      const res = await api.get<PaginatedMessages>(`/api/chat/sessions/${sessionId}/messages/`, {
        params: { page: pageParam },
      });
      return res.data;
    },
    initialPageParam: 1,
    getNextPageParam: (lastPage) => lastPage.next,
    enabled: sessionId !== null,
    staleTime: 0,
    refetchOnWindowFocus: false,
  });

  // Build display messages (same pattern as main Chat)
  const historyMessages: Message[] =
    messagesData?.pages
      .slice()
      .reverse()
      .flatMap((page) =>
        page.results
          .slice()
          .reverse()
          .map((m) => ({
            id: m.id,
            role: m.role,
            content: m.content,
            created_at: m.created_at,
            options: m.options || [],
            quotation: m.quotation,
          })),
      ) ?? [];

  const allMessages: Message[] = [...historyMessages];
  const newestHistoryTime =
    historyMessages.length > 0
      ? new Date(historyMessages[historyMessages.length - 1].created_at!).getTime()
      : 0;

  localMessages.forEach((localMsg) => {
    const localTime = localMsg.created_at ? new Date(localMsg.created_at).getTime() : Date.now();
    const existsById = localMsg.id && allMessages.some((m) => m.id === localMsg.id);
    if (!existsById && localTime >= newestHistoryTime) {
      allMessages.push(localMsg);
    }
  });

  const sendMessage = useMutation({
    mutationFn: async (content: string) => {
      const res = await api.post<ChatResponse>(`/api/chat/sessions/${sessionId}/messages/`, {
        messages: [{ role: 'user', content }],
      });
      return res.data;
    },
    onSuccess: (data) => {
      const botMsg: Message = {
        role: 'assistant',
        content: data.message,
        options: data.options,
        quotation: data.quotation,
        created_at: new Date().toISOString(),
      };
      setLocalMessages((prev) => [...prev, botMsg]);
      scrollToBottom();
      queryClient.invalidateQueries({ queryKey: ['deal-chat-messages', sessionId] }).then(() => {
        setTimeout(() => setLocalMessages([]), 100);
      });
    },
    onError: () => {
      setLocalMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: 'Sorry, there was an error. Please try again.',
        },
      ]);
    },
  });

  const scrollToBottom = useCallback((smooth = true) => {
    messagesEndRef.current?.scrollIntoView({ behavior: smooth ? 'smooth' : 'auto' });
  }, []);

  // Initial scroll + scroll on new messages
  useLayoutEffect(() => {
    if (isInitialMount.current && allMessages.length > 0) {
      scrollToBottom(false);
      isInitialMount.current = false;
    } else if (sendMessage.isPending) {
      scrollToBottom(true);
    }
  }, [allMessages.length, sendMessage.isPending, scrollToBottom]);

  // Textarea auto-resize
  useEffect(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [input]);

  const handleSend = () => {
    if (!input.trim() || sendMessage.isPending || !sessionId) return;
    const content = input.trim();
    setInput('');
    if (textareaRef.current) textareaRef.current.style.height = 'auto';

    // Add optimistic user message
    const userMsg: Message = { role: 'user', content, created_at: new Date().toISOString() };
    setLocalMessages((prev) => [...prev, userMsg]);

    // Auto-expand when sending first message
    if (!isExpanded && onToggleExpand) {
      onToggleExpand();
    }

    sendMessage.mutate(content);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  if (!sessionId) {
    return <div className={styles.empty}>No chat session linked</div>;
  }

  const isInitialLoading = messagesLoading;

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <span className={styles.headerTitle}>Chat: {dealTitle}</span>
        <div className={styles.headerActions}>
          {onToggleExpand && (
            <button
              className={styles.headerButton}
              onClick={onToggleExpand}
              title={isExpanded ? 'Collapse' : 'Expand'}
            >
              <FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} />
            </button>
          )}
          {onClose && (
            <button className={styles.headerButton} onClick={onClose} title="Close chat">
              <FontAwesomeIcon icon={faTimes} />
            </button>
          )}
        </div>
      </div>

      <div className={styles.messages} ref={messagesContainerRef}>
        {/* Loading skeleton */}
        {isInitialLoading && (
          <div className={styles.skeletonContainer}>
            <div className={styles.skeletonRow}>
              <div className={`${styles.skeletonAvatar} ${styles.skeletonBot}`} />
              <div className={`${styles.skeletonBubble} ${styles.skeletonBot}`} />
            </div>
            <div className={`${styles.skeletonRow} ${styles.skeletonReverse}`}>
              <div className={`${styles.skeletonAvatar} ${styles.skeletonUser}`} />
              <div className={`${styles.skeletonBubble} ${styles.skeletonUser}`} />
            </div>
            <div className={styles.skeletonRow}>
              <div className={`${styles.skeletonAvatar} ${styles.skeletonBot}`} />
              <div className={`${styles.skeletonBubble} ${styles.skeletonBot}`} />
            </div>
          </div>
        )}

        {/* Empty state */}
        {!isInitialLoading && allMessages.length === 0 && !sendMessage.isPending && (
          <div className={styles.emptyMessages}>
            Use the chat to get quotes and find providers for this deal.
          </div>
        )}

        {/* Messages */}
        {allMessages.map((msg, i) => (
          <MessageBubble key={msg.id ?? i} message={msg} />
        ))}

        {/* Thinking animation */}
        <ThinkingAnimation isPending={sendMessage.isPending} />

        <div ref={messagesEndRef} />
      </div>

      <div className={styles.inputArea}>
        <textarea
          ref={textareaRef}
          className={styles.textarea}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Type a message..."
          rows={1}
          disabled={sendMessage.isPending}
        />
        <button
          className={styles.sendButton}
          onClick={handleSend}
          disabled={!input.trim() || sendMessage.isPending}
          title="Send"
        >
          <FontAwesomeIcon icon={faPaperPlane} />
        </button>
      </div>
    </div>
  );
};
